<template>
  <div class="form-container">
    <v-card class="form-card" elevation="2">
      <!-- Cabeçalho -->
      <v-card-title class="form-header">
        <span class="form-title">{{ isEditMode ? 'Editar Unidade Orçamentária' : 'Cadastrar Unidade Orçamentária' }}</span>
        <v-checkbox
          v-model="formData.ativo"
          label="Ativo"
          color="primary"
          hide-details
          class="form-checkbox"
        />
      </v-card-title>

      <!-- Formulário -->
      <v-card-text class="form-content">
        <v-form ref="form" v-model="isFormValid">
          <!-- Linha 1: Exercício -->
          <v-row class="form-row">
            <v-col cols="12" sm="6" md="3" class="pa-1">
              <div class="form-label">Exercício</div>
              <v-select
                v-model="formData.exercicio"
                :items="exerciciosOptions"
                outlined
                dense
                :rules="[rules.required]"
                placeholder="Selecione um exercício"
                class="form-field"
                hide-details="auto"
                clearable
              />
            </v-col>
          </v-row>

          <!-- Linha 2: Código da Unidade e Nome -->
          <v-row class="form-row">
            <v-col cols="12" sm="6" md="3" class="pa-1">
              <div class="form-label">Código da Unidade</div>
              <v-text-field
                v-model="formData.codigo_unidade"
                type="text"
                outlined
                dense
                :rules="[rules.required, rules.numericOnly]"
                placeholder="Digite o código"
                class="form-field"
                hide-details="auto"
                @input="onNumericInput('codigo_unidade', $event)"
                @keypress="onlyNumbers"
                @paste="onPaste"
              />
            </v-col>
            <v-col cols="12" sm="6" md="9" class="pa-1">
              <div class="form-label">Nome da Unidade</div>
              <v-text-field
                v-model="formData.nome"
                outlined
                dense
                :rules="[rules.required]"
                placeholder="Digite o nome"
                class="form-field"
                hide-details="auto"
              />
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <!-- Rodapé com Botões -->
      <v-card-actions class="form-actions">
        <v-btn
          @click="handleCancel"
          class="action-btn cancel-btn grey-btn"
        >
          Cancelar
        </v-btn>
        <v-btn
          @click="handleSave"
          color="primary"
          class="action-btn save-btn"
          :loading="isSaving"
        >
          <v-icon left>mdi-content-save</v-icon>
          Salvar
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Snackbar para mensagens -->
    <v-snackbar
      v-model="showSnackbar"
      :color="snackbarColor"
      :timeout="3000"
      top
    >
      {{ snackbarMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="showSnackbar = false"
        >
          Fechar
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { ExercicioService } from "../../services/ExercicioService";

export default {
  name: 'UnidadeOrcamentariaForm',
  props: {
    initialData: {
      type: Object,
      default: () => ({})
    },
    isEditMode: {
      type: Boolean,
      default: false
    },
    unidadeOrcamentariaId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      isFormValid: false,
      isSaving: false,
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',
      formData: {
        exercicio: '',
        codigo_unidade: '',
        nome: '',
        ativo: true
      },
      rules: {
        required: v => !!v || 'Campo obrigatório',
        numericOnly: v => /^\d*$/.test(v) || 'Apenas números são permitidos'
      },
      exerciciosOptions: []
    }
  },
  created() {
    this.listarExerciciosSelect();
  },
  watch: {
    initialData: {
      handler(newData) {
        if (newData && Object.keys(newData).length > 0) {
          this.formData = {
            exercicio: newData.exercicio?.ano || '',
            codigo_unidade: newData.codigo_unidade || '',
            nome: newData.nome || '',
            ativo: newData.ativo !== undefined ? Boolean(newData.ativo) : true
          };
        }
      },
      immediate: true
    }
  },
  methods: {
    async listarExerciciosSelect() {
      try {
        const response = await ExercicioService.select();
        if (response) {
          this.exerciciosOptions = response.data.map((el) => {
            return {
              text: el.ano,
              value: el.ano,
            };
          });
          this.exerciciosOptions?.unshift({
            text: "Selecione",
            value: "",
          });
        } else {
          console.error('Não foi possível obter os exercícios.');
        }
      } catch (error) {
        console.error('Ocorreu um erro ao listar os exercícios:', error);
      }
    },
    validateForm() {
      const isValid = this.$refs.form.validate();
      return isValid;
    },
    async handleSave() {
      if (!this.validateForm()) {
        this.showErrorMessage('Por favor, preencha todos os campos obrigatórios');
        return;
      }

      this.isSaving = true;
      
      try {
        await this.$emit('save', this.formData);
      } catch (error) {
        this.showErrorMessage('Erro ao salvar a unidade orçamentária. Tente novamente.');
      } finally {
        this.isSaving = false;
      }
    },
    handleCancel() {
      this.$emit('cancel');
    },
    showErrorMessage(message) {
      this.snackbarColor = 'error';
      this.snackbarMessage = message;
      this.showSnackbar = true;
    },
    // Método para permitir apenas números na digitação
    onlyNumbers(event) {
      const charCode = event.which ? event.which : event.keyCode;
      
      // Bloquear explicitamente pontos e outros caracteres especiais
      if (charCode === 190 || charCode === 110 || charCode === 188 || charCode === 46) {
        event.preventDefault();
        return false;
      }
      
      // Permitir apenas números (48-57) e teclas especiais de navegação
      if (charCode < 48 || charCode > 57) {
        // Permitir apenas teclas especiais: backspace (8), tab (9), enter (13), setas (37-40)
        if (charCode !== 8 && charCode !== 9 && charCode !== 13 && 
            charCode !== 37 && charCode !== 38 && charCode !== 39 && charCode !== 40) {
          event.preventDefault();
          return false;
        }
      }
      
      return true;
    },
    // Método para interceptar colagem e filtrar apenas números
    onPaste(event) {
      event.preventDefault();
      const pastedText = (event.clipboardData || window.clipboardData).getData('text');
      const numericOnly = pastedText.replace(/[^0-9]/g, '');
      this.formData.codigo_unidade = numericOnly;
    },
    // Método para filtrar entrada e manter apenas números
    onNumericInput(fieldName, value) {
      // Remove qualquer caractere que não seja número
      const numericValue = value.replace(/[^0-9]/g, '');
      this.formData[fieldName] = numericValue;
    }
  }
}
</script>

<style scoped>
.form-container {
  padding: 8px;
  max-width: 1400px;
  margin: 0 auto;
}

.form-card {
  border-radius: 8px;
  overflow: hidden;
}

.form-header {
  background-color: #F8FAFC;
  border-bottom: 1px solid #E5E7EB;
  padding: 16px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.form-title {
  font-size: 1.375rem;
  font-weight: 700;
  color: #1F2937;
}

.form-checkbox {
  margin: 0;
}

.form-content {
  padding: 16px 20px;
}

.form-row {
  margin: 0;
}

.form-field {
  margin: 0;
}

.form-actions {
  background-color: #F8FAFC;
  border-top: 1px solid #E5E7EB;
  padding: 16px 20px;
  display: flex;
  justify-content: center;
  gap: 16px;
}

.action-btn {
  min-width: 120px;
  height: 40px;
  font-weight: 600;
  text-transform: none;
  border-radius: 6px;
}

.cancel-btn {
  border: 2px solid #9CA3AF;
  color: #6B7280;
}

.cancel-btn:hover {
  background-color: #F3F4F6;
  border-color: #6B7280;
}

.save-btn {
  background-color: #16A34A;
  color: white;
}

.save-btn:disabled {
  background-color: #D1D5DB;
  color: #9CA3AF;
}

/* Responsividade */
@media (max-width: 960px) {
  .form-container {
    padding: 6px;
  }
  
  .form-header {
    padding: 12px 16px;
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
  
  .form-content {
    padding: 12px 16px;
  }
  
  .form-actions {
    padding: 12px 16px;
    flex-direction: column;
    align-items: center;
  }
  
  .action-btn {
    width: 100%;
    max-width: 200px;
  }
}

@media (max-width: 600px) {
  .form-container {
    padding: 4px;
  }
  
  .form-header {
    padding: 10px 12px;
  }
  
  .form-content {
    padding: 10px 12px;
  }
  
  .form-actions {
    padding: 10px 12px;
  }
  
  .form-title {
    font-size: 1.25rem;
  }
}
</style>
